// app/frontend/hooks/useConversationDraft.ts

import { useCallback, useRef, useState } from 'react'

export type DraftStash = Record<string, string>

/** Stores a draft against its conversation, dropping it when it's blank. */
export function stashDraft(stash: DraftStash, conversationId: string, draft: string): DraftStash {
  const next = { ...stash }
  if (draft.trim()) {
    next[conversationId] = draft
  } else {
    delete next[conversationId]
  }
  return next
}

export function takeDraft(stash: DraftStash, conversationId: string): string {
  return stash[conversationId] ?? ''
}

/**
 * Keeps the composer draft tied to the conversation it was written in.
 *
 * The Conversation page component is reused when the agent switches
 * conversations (Inertia swaps props, React keeps the tree mounted), so a
 * single piece of draft state follows the agent around and can be sent to the
 * wrong visitor. Drafts are stashed per conversation id and swapped during
 * render, so the composer never shows another conversation's text — not even
 * for a frame.
 */
export function useConversationDraft(conversationId: string) {
  const stash = useRef<DraftStash>({})
  const activeId = useRef(conversationId)
  const [renderedId, setRenderedId] = useState(conversationId)
  const [draft, setDraft] = useState('')

  if (renderedId !== conversationId) {
    stash.current = stashDraft(stash.current, renderedId, draft)
    setRenderedId(conversationId)
    setDraft(takeDraft(stash.current, conversationId))
  }

  // Kept in sync during render (not a passive effect) so a send that resolves
  // between this render committing and its effects flushing still sees the
  // conversation the agent is actually looking at.
  activeId.current = conversationId

  // Defaults to the conversation this render belongs to, so a send that
  // resolves after the agent has moved on clears the sent draft, not the
  // one they're typing now.
  const clearDraft = useCallback((id: string = conversationId) => {
    stash.current = stashDraft(stash.current, id, '')
    if (id === activeId.current) setDraft('')
  }, [conversationId])

  return { draft, setDraft, clearDraft }
}
